import { useCallback, useEffect, useState } from 'react';
import { OrgInfo } from '../../../common/src/model.ts';
import * as server_api from '../../../common/src/server_api.ts';
import { OneSecondMs } from '../../../common/src/sleep.ts';
import { useCurrentDate } from '../util/use_current_date.tsx';
import { LocalStorageKey } from './client_local_storage.ts';
import { useCurrentOrg } from './use_current_org.tsx';
import { useLocalStorage } from './use_local_storage.tsx';

const serverRetryIntervalSeconds = 5;

export function useOrgInfo(): { orgInfo?: OrgInfo; isDataRecentlySynced: boolean; updateOrgInfo: () => Promise<void> } {
  const { currentOrg } = useCurrentOrg();
  const [orgInfo, setOrgInfo] = useLocalStorage(LocalStorageKey.OrgInfo);
  const [isDataRecentlySynced, setIsDataRecentlySynced] = useState(false);
  const today = useCurrentDate();

  const updateOrgInfo = useCallback(async () => {
    if (!currentOrg) {
      setIsDataRecentlySynced(true);
      return;
    }
    setIsDataRecentlySynced(false);
    const serverOrgInfo = await server_api.getOrgInfo(currentOrg.id, today);
    setOrgInfo(serverOrgInfo);
    setIsDataRecentlySynced(true);
  }, [currentOrg, setOrgInfo, today]);

  useEffect(() => {
    let retryTimeout: ReturnType<typeof setTimeout> | undefined;
    void (async () => {
      try {
        await updateOrgInfo();
      } catch {
        retryTimeout = setTimeout(updateOrgInfo, serverRetryIntervalSeconds * OneSecondMs);
      }
    })();
    return () => {
      if (retryTimeout !== undefined) clearTimeout(retryTimeout);
    };
  }, [updateOrgInfo]);

  return { orgInfo, isDataRecentlySynced, updateOrgInfo };
}
